import { useRef } from "react";
import { useTheme } from "../ThemeProvider";
import usePermiso from "../../hooks/usePermiso";
import { usePropiedadUi } from "../../hooks/usePropiedadUi";
import EtiquetasFlotantesPropiedad, { AnclaEtiquetasPropiedad } from "./EtiquetasFlotantesPropiedad";

export default function SeccionCabecera(props) {
  const { form, cambiar, cambiarDirecto, auxiliares, camposError, onIrAPrecio, esEdicion = false, soloLecturaIdioma = false } = props;
  const { t, tv } = usePropiedadUi(props);
  const etiquetasAnchorRef = useRef(null);
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const puedeVerDisponible = puede("disponible", "ver");
  const puedeModificarDisponible = puede("disponible", "modificar");
  const puedeVerFechaAlta = puede("fecha_alta", "ver");
  const puedeVerExclusiva = puede("exclusiva", "ver");
  const puedeModificarExclusiva = puede("exclusiva", "modificar");
  const puedeVerPublicarWeb = puede("publicar_web", "ver");
  const puedeModificarPublicarWeb = puede("publicar_web", "modificar");
  const puedeVerAConsultar = puede("a_consultar", "ver");
  const puedeModificarAConsultar = puede("a_consultar", "modificar");
  const puedeVerOperacion = puede("operacion", "ver");
  const puedeModificarOperacion = puede("operacion", "modificar");
  const puedeVerTipoInmueble = puede("tipo_inmueble", "ver");
  const puedeModificarTipoInmueble = puede("tipo_inmueble", "modificar");
  const puedeVerEtiqueta = puede("etiqueta", "ver");
  const puedeModificarEtiqueta = puede("etiqueta", "modificar");
  const puedeVerReferencia = puede("referencia", "ver");
  const puedeModificarReferencia = puede("referencia", "modificar");
  const tipos = auxiliares["tipo-inmueble"] || [];
  const etiquetas = auxiliares["etiqueta"] || [];

  const hasError = (name) => camposError?.includes(name);

  // ── Estilos base ────────────────────────────────────────────────────────────
  const labelStyle = {
    fontSize: 11, fontWeight: 600, color: c.textMuted,
    textTransform: "uppercase", letterSpacing: "0.04em",
    marginBottom: 6, display: "block",
  };
  const inputStyle = (err) => ({
    padding: "8px 12px",
    border: `1px solid ${err ? c.accentRed : c.chipBorder}`,
    borderRadius: 8, fontSize: 13,
    color: c.textPrimary,
    background: err ? (isDark ? '#2d1515' : '#fff5f5') : (isDark ? '#0a0e16' : '#dceeff'),
    boxShadow: err ? 'none' : (isDark ? 'inset 0 3px 6px rgba(0,0,0,0.5), inset 0 1px 3px rgba(0,0,0,0.3)' : 'inset 0 3px 6px rgba(100,140,200,0.18), inset 0 1px 3px rgba(80,120,180,0.13)'),
    width: "100%", outline: "none",
  });

  // ── Chip idéntico al de FichaCliente ────────────────────────────────────────
  const LINE_CTRL_H = 28;
  const chipStyle = (activo) => ({
    padding: "4px 10px",
    fontSize: 11,
    fontWeight: 500,
    borderRadius: 6,
    border: `1px solid ${activo ? c.chipActiveBorder : c.chipBorder}`,
    background: activo ? c.chipActiveBg : c.chipBg,
    color: activo ? c.chipActiveText : c.chipText,
    cursor: "pointer",
    transition: "all 0.15s ease",
    userSelect: "none",
    lineHeight: "1.4",
    minHeight: LINE_CTRL_H,
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  });
  const compactInputStyle = (err) => ({
    ...inputStyle(err),
    padding: "4px 10px",
    fontSize: 11,
    lineHeight: "1.4",
    minHeight: LINE_CTRL_H,
    boxSizing: "border-box",
    width: "auto",
  });

  const onEnter = (e, activo) => {
    if (!activo) {
      e.currentTarget.style.background = c.chipHoverBg;
      e.currentTarget.style.color = c.chipHoverText;
      e.currentTarget.style.borderColor = c.chipHoverBorder;
    }
  };
  const onLeave = (e, activo) => {
    if (!activo) {
      e.currentTarget.style.background = c.chipBg;
      e.currentTarget.style.color = c.chipText;
      e.currentTarget.style.borderColor = c.chipBorder;
    }
  };

  // ── Toggle deslizante ────────────────────────────────────────────────────────
  const Toggle = ({ name, label, disabled = false }) => {
    const checked = form[name] || false;
    return (
      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: disabled ? "not-allowed" : "pointer", userSelect: "none", opacity: disabled ? 0.6 : 1 }}>
        <div
          onClick={() => { if (!disabled) cambiarDirecto(name, !checked); }}
          title={disabled ? "No tienes permiso para modificar" : undefined}
          style={{
            width: 34, height: 20, borderRadius: 10,
            background: checked ? c.chipActiveBg : c.border,
            position: "relative", transition: "background 0.2s",
            flexShrink: 0, cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          <div style={{
            width: 14, height: 14, borderRadius: "50%", background: "#fff",
            position: "absolute", top: 3, left: checked ? 17 : 3,
            transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          }} />
        </div>
        <span style={{ fontSize: 13, color: c.textPrimary, fontWeight: checked ? 600 : 400 }}>{label}</span>
      </label>
    );
  };

  return (
    <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 20 }}>

      {/* FILA 1: toggles de estado */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-end" }}>
        {puedeVerDisponible && (
        <div>
          <label style={labelStyle}>{tv("Estado")}</label>
          <Toggle name="disponible" label={form.disponible ? tv("Disponible") : tv("No disponible")} disabled={!puedeModificarDisponible} />
        </div>
        )}
        {puedeVerDisponible && (!form.disponible ? (
          <>
            <div style={{ minWidth: 160 }}>
              <label style={labelStyle}>{tv("Disponible de nuevo el")}</label>
              <input
                type="date"
                name="disponible_desde"
                value={form.disponible_desde || ""}
                onChange={cambiar}
                disabled={!puedeModificarDisponible}
                style={{ ...inputStyle(false), opacity: puedeModificarDisponible ? 1 : 0.6, cursor: puedeModificarDisponible ? "text" : "not-allowed" }}
              />
            </div>
            
            <div style={{ flex: "1 1 220px", minWidth: 200 }}>
              <label style={labelStyle}>{tv("Motivo")}</label>
              <input
                type="text"
                name="motivo_no_disponible"
                value={form.motivo_no_disponible || ""}
                onChange={cambiar}
                placeholder="Ej. reforma, ocupado, acuerdo con propietario…"
                disabled={!puedeModificarDisponible}
                style={{ ...inputStyle(false), opacity: puedeModificarDisponible ? 1 : 0.6, cursor: puedeModificarDisponible ? "text" : "not-allowed" }}
              />
            </div>
          </>
        ) : (
          <div style={{ minWidth: 150 }}>
            <label style={labelStyle}>{tv("Disponible desde")}</label>
            <input
              type="date"
              name="disponible_desde"
              value={form.disponible_desde || ""}
              onChange={cambiar}
              disabled={!puedeModificarDisponible}
              style={{ ...inputStyle(false), opacity: puedeModificarDisponible ? 1 : 0.6, cursor: puedeModificarDisponible ? "text" : "not-allowed" }}
            />
          </div>
        ))}
        {puedeVerFechaAlta && (
        <div style={{ minWidth: 150 }}>
          <label style={labelStyle}>{tv("Fecha de alta")}</label>
          <input type="date" name="fecha_alta" value={form.fecha_alta || ""} onChange={cambiar} style={inputStyle(false)} />
        </div>
        )}
        <div style={{ minWidth: 170 }}>
          <label style={labelStyle}>{tv("Última actualización")}</label>
          <input
            type="text"
            readOnly
            value={(() => {
              const v = form.updated_at;
              if (!v) return "—";
              const d = new Date(v);
              if (Number.isNaN(d.getTime())) return "—";
              return d.toLocaleString("es-ES", {
                day: "2-digit", month: "2-digit", year: "numeric",
                hour: "2-digit", minute: "2-digit",
              });
            })()}
            style={{ ...inputStyle(false), cursor: "default", opacity: 0.85 }}
          />
        </div>
        {puedeVerExclusiva && (
        <div>
          <label style={labelStyle}>{tv("Exclusiva")}</label>
          <Toggle name="exclusiva" label={tv("Exclusiva")} disabled={!puedeModificarExclusiva} />
        </div>
        )}
        {puedeVerPublicarWeb && (
        <div>
          <label style={labelStyle}>{tv("Publicación")}</label>
          <Toggle name="publicar_web" label={tv("Publicar en web")} disabled={!puedeModificarPublicarWeb} />
        </div>
        )}
        {puedeVerAConsultar && (
        <div>
          <label style={labelStyle}>{tv("Precio")}</label>
          <Toggle name="precio_web_consultar" label={tv("A consultar")} disabled={!puedeModificarAConsultar} />
        </div>
        )}
      </div>

      {/* Datos exclusiva */}
      {puedeVerExclusiva && form.exclusiva && (
        <div style={{
          display: "flex", flexWrap: "wrap", gap: 12,
          background: isDark ? "#1a1500" : "#fffbeb",
          border: `1px solid #d97706`, borderRadius: 10, padding: "14px 16px",
        }}>
          <div>
            <label style={labelStyle}>{tv("Duración")}{form.exclusiva ? <span style={{ color: "#b91c1c" }}> *</span> : null}</label>
            <select
              name="exclusiva_duracion"
              value={form.exclusiva_duracion || ""}
              onChange={cambiar}
              disabled={!puedeModificarExclusiva}
              required={!!form.exclusiva}
              title={!puedeModificarExclusiva ? "No tienes permiso para modificar" : undefined}
              style={{ ...inputStyle(false), width: "auto", cursor: puedeModificarExclusiva ? "pointer" : "not-allowed", opacity: puedeModificarExclusiva ? 1 : 0.6 }}
            >
              <option value="">{tv("Seleccionar")}</option>
              {["1 mes","2 meses","3 meses","6 meses","12 meses"].map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 140 }}>
            <label style={labelStyle}>{tv("Desde")}</label>
            <input
              type="date"
              name="exclusiva_desde"
              value={form.exclusiva_desde || ""}
              onChange={cambiar}
              disabled={!puedeModificarExclusiva}
              style={{ ...inputStyle(false), opacity: puedeModificarExclusiva ? 1 : 0.6, cursor: puedeModificarExclusiva ? "text" : "not-allowed" }}
            />
          </div>
          <div style={{ minWidth: 140 }}>
            <label style={labelStyle}>{tv("Hasta")}{form.exclusiva ? <span style={{ color: "#b91c1c" }}> *</span> : null}</label>
            <input
              type="date"
              name="exclusiva_hasta"
              value={form.exclusiva_hasta || ""}
              onChange={cambiar}
              disabled={!puedeModificarExclusiva}
              required={!!form.exclusiva}
              style={{ ...inputStyle(false), opacity: puedeModificarExclusiva ? 1 : 0.6, cursor: puedeModificarExclusiva ? "text" : "not-allowed" }}
            />
          </div>
        </div>
      )}

      {/* OPERACIÓN */}
      {puedeVerOperacion && (
      <div>
        <label style={{ ...labelStyle, color: hasError("operacion") ? c.accentRed : c.textMuted }}>
          {t("operacion", "Operación")} <span style={{ color: c.accentRed }}>*</span>
        </label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["Venta", "Alquiler", "Traspaso"].map(op => {
            const activo = form.operacion === op;
            return (
              <button key={op} type="button"
                onClick={() => puedeModificarOperacion && cambiarDirecto("operacion", activo ? "" : op)}
                disabled={!puedeModificarOperacion}
                title={!puedeModificarOperacion ? "No tienes permiso para modificar" : undefined}
                style={{ ...chipStyle(activo), cursor: puedeModificarOperacion ? "pointer" : "not-allowed", opacity: puedeModificarOperacion ? 1 : 0.6 }}
                onMouseEnter={e => puedeModificarOperacion && onEnter(e, activo)}
                onMouseLeave={e => puedeModificarOperacion && onLeave(e, activo)}
              >
                {tv(op)}
              </button>
            );
          })}
        </div>
        {hasError("operacion") && <span style={{ fontSize: 11, color: c.accentRed, marginTop: 4, display: "block" }}>{tv("Selecciona una operación")}</span>}
      </div>
      )}

      {/* TIPO DE INMUEBLE */}
      {puedeVerTipoInmueble && (
      <div id="campo-tipo_inmueble" style={{ scrollMarginTop: 120 }}>
        <label style={{ ...labelStyle, color: hasError("tipo_inmueble_id") ? c.accentRed : c.textMuted }}>
          {t("tipo_inmueble_nombre", "Tipo de inmueble")} <span style={{ color: c.accentRed }}>*</span>
        </label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {tipos.map((tipo) => {
            const activo = String(form.tipo_inmueble_id) === String(tipo.id);
            return (
              <button key={tipo.id} type="button"
                onClick={() => puedeModificarTipoInmueble && cambiarDirecto("tipo_inmueble_id", activo ? "" : tipo.id)}
                disabled={!puedeModificarTipoInmueble}
                title={!puedeModificarTipoInmueble ? "No tienes permiso para modificar" : undefined}
                style={{ ...chipStyle(activo), cursor: puedeModificarTipoInmueble ? "pointer" : "not-allowed", opacity: puedeModificarTipoInmueble ? 1 : 0.6 }}
                onMouseEnter={e => puedeModificarTipoInmueble && onEnter(e, activo)}
                onMouseLeave={e => puedeModificarTipoInmueble && onLeave(e, activo)}
              >
                {soloLecturaIdioma && form.tipo_inmueble_nombre && activo
                  ? form.tipo_inmueble_nombre
                  : tipo.nombre}
              </button>
            );
          })}
        </div>
        {hasError("tipo_inmueble_id") && <span style={{ fontSize: 11, color: c.accentRed, marginTop: 4, display: "block" }}>{tv("Selecciona el tipo de inmueble")}</span>}
      </div>
      )}

      {/* ETIQUETA + TÍTULO + REF */}
      <div style={{ display: "grid", gridTemplateColumns: (puedeVerEtiqueta && etiquetas.length > 0) ? "auto auto" : "auto", gap: 16, alignItems: "end" }}>

        {/* Etiqueta */}
        {puedeVerEtiqueta && etiquetas.length > 0 && (
          <div>
            <label style={labelStyle}>{tv("Etiqueta")}</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {etiquetas.map(e => {
                const activo = String(form.etiqueta_id) === String(e.id);
                return (
                  <button key={e.id} type="button"
                    onClick={() => puedeModificarEtiqueta && cambiarDirecto("etiqueta_id", activo ? "" : e.id)}
                    disabled={!puedeModificarEtiqueta}
                    title={!puedeModificarEtiqueta ? "No tienes permiso para modificar" : undefined}
                    style={{ ...chipStyle(activo), cursor: puedeModificarEtiqueta ? "pointer" : "not-allowed", opacity: puedeModificarEtiqueta ? 1 : 0.6 }}
                    onMouseEnter={ev => puedeModificarEtiqueta && onEnter(ev, activo)}
                    onMouseLeave={ev => puedeModificarEtiqueta && onLeave(ev, activo)}
                  >
                    {e.nombre}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* REF */}
        {puedeVerReferencia && (
        <div style={{ width: 132, flexShrink: 0 }}>
          <label style={labelStyle}>{tv("Referencia")}</label>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {form.referencia_manual
              ? <input
                  type="text"
                  name="referencia"
                  id="campo-referencia"
                  value={form.referencia || ""}
                  onChange={cambiar}
                  placeholder="Manual"
                  disabled={!puedeModificarReferencia}
                  style={{ ...compactInputStyle(false), width: 72, flex: "none", opacity: puedeModificarReferencia ? 1 : 0.6, cursor: puedeModificarReferencia ? "text" : "not-allowed" }}
                />
              : <input type="text" id="campo-referencia" value={form.referencia || ""} readOnly placeholder="Auto"
                  style={{ ...compactInputStyle(false), width: 72, flex: "none", color: c.textMuted, background: isDark ? "#0e1117" : "#f8fafc" }} />
            }
            <button type="button"
              onClick={() => {
                if (!puedeModificarReferencia) return;
                const pasandoAManual = !form.referencia_manual;
                cambiarDirecto("referencia_manual", pasandoAManual);
                if (form.referencia_manual && !esEdicion) {
                  cambiarDirecto("referencia", "");
                }
              }}
              disabled={!puedeModificarReferencia}
              title={!puedeModificarReferencia ? "No tienes permiso para modificar" : undefined}
              style={{ ...chipStyle(false), whiteSpace: "nowrap", cursor: puedeModificarReferencia ? "pointer" : "not-allowed", opacity: puedeModificarReferencia ? 1 : 0.6 }}
              onMouseEnter={e => puedeModificarReferencia && onEnter(e, false)}
              onMouseLeave={e => puedeModificarReferencia && onLeave(e, false)}
            >
              {form.referencia_manual ? tv("Auto") : tv("Manual")}
            </button>
          </div>
        </div>
        )}

      </div>

      </div>

      <AnclaEtiquetasPropiedad anchorRef={etiquetasAnchorRef} />
      <EtiquetasFlotantesPropiedad form={form} onIrAPrecio={onIrAPrecio} anchorRef={etiquetasAnchorRef} />

    </div>
  );
}
